/**
 * UserFiltersDrawer
 *
 * Drawer with filter fields for the users table:
 * email, profile (single-select), and active status.
 *
 * ## Component Hierarchy
 * - **Level**: Molecule
 * - **Feature**: Users
 *
 * @module features/users/components/ui/molecules/UserFiltersDrawer
 */
import React, { useMemo } from 'react';
import { Drawer, Input, Select, Space, Typography } from 'antd';

import { useLanguage } from '@/shared/hooks/useLanguage';
import { getProfileI18nKey } from '@/features/users/utils/profileLabels';
import type {
  ExternalProfile,
  UserFilters,
} from '@/features/users/types/users.types';

import './UserFiltersDrawer.css';
import { Button } from '@/shared/components/ui/atoms';

export interface UserFiltersDrawerProps {
  /** Whether the drawer is visible */
  open: boolean;
  /** Draft filter values (controlled) */
  draftFilters: UserFilters;
  /** Available external profiles (fetched from API) */
  profiles?: ExternalProfile[];
  /** Setter for draft filter values */
  onDraftChange: (filters: UserFilters) => void;
  /** Called to close the drawer without applying */
  onClose: () => void;
  /** Called to clear all filters */
  onClear: () => void;
  /** Called to apply the current draft filters */
  onApply: () => void;
}

/**
 * UserFiltersDrawer Component
 *
 * @param props - Component properties
 * @returns A Drawer with user-specific filter controls
 *
 * @example
 * ```tsx
 * <UserFiltersDrawer
 *   open={isOpen}
 *   draftFilters={draftFilters}
 *   onDraftChange={setDraftFilters}
 *   onClose={handleClose}
 *   onClear={handleClear}
 *   onApply={handleApply}
 * />
 * ```
 */
export const UserFiltersDrawer: React.FC<UserFiltersDrawerProps> = ({
  open,
  draftFilters,
  profiles,
  onDraftChange,
  onClose,
  onClear,
  onApply,
}) => {
  const { t } = useLanguage();

  const profileOptions = useMemo(() => {
    const dynamicOptions = (profiles ?? []).map((p) => ({
      label: t(getProfileI18nKey(p.name)),
      value: p.name,
    }));
    return [...dynamicOptions, { label: t('No Profile'), value: 'NO_PROFILE' }];
  }, [profiles, t]);

  return (
    <Drawer
      title={t('Filters')}
      open={open}
      width={630}
      onClose={onClose}
      placement="right"
      extra={
        <Button variant="link" onClick={onClear}>
          {t('Clear')}
        </Button>
      }
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button variant="ghost" onClick={onClear}>
            {t('Clear')}
          </Button>
          <Space>
            <Button variant="ghost" onClick={onClose}>
              {t('Close')}
            </Button>
            <Button variant="primary" onClick={onApply}>
              {t('Apply filters')}
            </Button>
          </Space>
        </Space>
      }
    >
      <div className="user-filters-drawer">
        <div>
          <Typography.Text>{t('Email')}</Typography.Text>
          <Input
            value={draftFilters.email}
            onChange={(e) =>
              onDraftChange({ ...draftFilters, email: e.target.value })
            }
            placeholder={t('Enter email')}
            aria-label={t('Filter by email')}
          />
        </div>

        <div>
          <Typography.Text>{t('Profile')}</Typography.Text>
          <Select
            allowClear
            value={draftFilters.profileName}
            onChange={(value) =>
              onDraftChange({ ...draftFilters, profileName: value })
            }
            options={profileOptions}
            placeholder={t('Select profile')}
            aria-label={t('Filter by profile')}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default UserFiltersDrawer;
